import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { AuthService } from '../../services/auth.service';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { TimeoutError } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { Router, ActivatedRoute } from '@angular/router';
import { User } from '../../models/user.model';
import { UtilsService } from 'src/app/shared/services/utils.service';
import { GtagService } from 'src/app/gtag/gtag.service';
import { ErrorsService } from '../../../shared/services/errors.service';



@Component({
  selector: 'hs-signup',
  templateUrl: './signup.component.html',
  styleUrls: ['./signup.component.scss']
})
export class SignupComponent implements OnInit {


  @Input() redirect = true;
  @Input() cart = false;
  @Input() showButton = true;
  @Input() externalEmail = false;
  @Output() signUpSuccess = new EventEmitter<User>();
  @Output() signUpError = new EventEmitter<string>();
  @Output() signUploading = new EventEmitter<boolean>();

  registerForm: FormGroup;
  submitted = false;
  success = false;
  username = '';
  loading = false;

  error = null;
  timeoutError = false;

  returnUrl: string;

  constructor(
    private auth: AuthService,
    private formBuilder: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private utils: UtilsService,
    private gtag: GtagService,
    private errorsServie: ErrorsService
  ) { }

  ngOnInit(): void {

    const returnUrlParam = 'returnUrl';
    this.returnUrl = this.route.snapshot.queryParams[returnUrlParam] || '/';
    if (this.auth.currentUser) {
      this.router.navigate(['/']);
    }
    if (this.externalEmail) {
      this.registerForm = this.formBuilder.group({
        username: ['', Validators.required],
        password: ['', [Validators.required, Validators.minLength(3)]],
        confirmPassword: ['', Validators.required],
      }, {
        validator: this.mustMatch('password', 'confirmPassword')
      });
    } else {
      this.registerForm = this.formBuilder.group({
        username: ['', Validators.required],
        email: ['', [Validators.required, Validators.email]],
        password: ['', [Validators.required, Validators.minLength(3)]],
        confirmPassword: ['', Validators.required],
      }, {
        validator: this.mustMatch('password', 'confirmPassword')
      });
    }
  }

  /**
   * Used to validate that two strings are equal, sets an error in the form if they are not
   */
  mustMatch(controlName: string, matchingControlName: string) {
    return (formGroup: FormGroup) => {
      const control = formGroup.controls[controlName];
      const matchingControl = formGroup.controls[matchingControlName];

      if (matchingControl.errors && !matchingControl.errors.mustMatch) {
        // return if another validator has already found an error on the matchingControl
        return;
      }
      // set error on matchingControl if validation fails
      if (control.value !== matchingControl.value) {
        control.setErrors({ error: true });
        matchingControl.setErrors({ mustMatch: true });
      } else {
        control.setErrors(null);
        matchingControl.setErrors(null);
      }
    };
  }

  get f() {
    return this.registerForm.controls;
  }

  isFormInvalid() {
    this.submitted = true;
    this.timeoutError = false;
    this.success = false;

    return this.registerForm.invalid;
  }

  signUp(email: string = null) {
    this.gtag.signUp();
    this.submitted = true;
    this.timeoutError = false;
    this.success = false;

    if (this.registerForm.invalid) {
      // return if an error is found
      return;
    }

    // show spinner while waiting for the server response
    this.loading = true;
    this.signUploading.emit(this.loading);
    this.error = null;
    this.timeoutError = false;
    this.registerForm.disable();
    this.loading = true;

    this.auth.signUp(this.registerForm.value.username,
      email ?? this.registerForm.value.email,
      this.registerForm.value.password,
      this.registerForm.value.confirmPassword).subscribe(response => {
        this.success = true;
        this.username = response.displayName;
        // reset form and hide spinner when the data has arrived
        this.registerForm.reset();
        this.submitted = false;
        this.loading = false;
        this.signUploading.emit(this.loading);
        this.registerForm.enable();
        this.signUpSuccess.emit(response);
        if (this.redirect) {
          this.router.navigate([this.returnUrl]);
          window.scroll(0, 0);
        }
      }, error => {
        // show error if user exists or if there is another generic error
        if (error instanceof TimeoutError) {
          this.timeoutError = true;
        } else if (error instanceof HttpErrorResponse) {
          this.error = error.error;
        }
        this.submitted = false;
        this.registerForm.enable();
        this.loading = false;
        this.signUploading.emit(this.loading);
        this.signUpError.emit(error?.error?.message ?? error?.errorMsg ?? error);
        this.errorsServie.setContext('user_data', {
          username: this.registerForm.value.username,
          email: this.registerForm.value.email
        });
        this.errorsServie.track(error);
      });
  }

}
